import React, { useState, useEffect, useCallback, useRef, useMemo, Suspense, lazy } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getFirestore, doc, getDoc, query, collection, where, getDocs, limit, deleteDoc, setDoc, updateDoc, increment, addDoc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import 'react-lazy-load-image-component/src/effects/blur.css';
import { useAuth } from '../contexts/AuthContext';
import { useComparison } from '../contexts/ComparisonContext';
import { usePermissions } from '../hooks/usePermissions';
import SEO from '../components/SEO';
import { useCompanySettings } from '../hooks/useCompanySettings';
import ScrollToTop from '../components/ScrollToTop';
import PreLoader from '../components/PreLoader';
import PropertyDetailsSkeleton from '../components/PropertyDetailsSkeleton';
import { showNotification } from '../components/Notification';
import { logPropertyDelete, logPropertyUpdate, buildPropertyChangeDiff } from '../utils/logger';
import { trackPropertyView, trackWhatsAppClick } from '../services/analyticsService';
import { getActiveCondoDetailItems } from '../constants/condoDetails';
import { useCondoDetailsCustomCatalog } from '../hooks/useCondoDetailsCustomCatalog';
import { mergeCondoDetailsCustomCatalog } from '../services/condoDetailsCustomService';
import { getActivePropertyDetailItems } from '../constants/propertyDetailLabels';
import { usePropertyDetailsCustomCatalog } from '../hooks/usePropertyDetailsCustomCatalog';
import { mergePropertyDetailsCustomCatalog } from '../services/propertyDetailsCustomService';
import { getPropertyDisplayAddress } from '../utils/propertyAddress';
import BreadcrumbNav from '../components/seo/BreadcrumbNav';
import PropertyInternalLinks from '../components/seo/PropertyInternalLinks';
import PropertyContactActions from '../components/PropertyContactActions';
import { fetchPropertiesCatalog, fetchHomeFeaturedProperties } from '../services/propertiesCatalog';
import {
  buildPropertyBreadcrumbs,
  buildPropertyBreadcrumbSchema,
  generatePropertyImageAlt,
  resolveOfficialBaseUrl,
  buildPropertyPath,
  buildPropertyUrl,
  extractPropertyIdFromParam,
} from '../utils/seo';

const QuickEditModal = lazy(() => import('../components/QuickEditModal'));
const FinancingSimulator = lazy(() => import('../components/FinancingSimulator'));

function PropertyDetails({ propertyId = null }) {
  const params = useParams();
  const id =
    propertyId ||
    extractPropertyIdFromParam(params.slugAndId || params.seg1) ||
    null;
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isPreLoading, setIsPreLoading] = useState(true);
  const [showGallery, setShowGallery] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isSlideshowActive, setIsSlideshowActive] = useState(false);
  const [slideshowProgress, setSlideshowProgress] = useState(0);
  const [galleryTab, setGalleryTab] = useState('images');
  const [, setPreloadedImages] = useState([]);
  const [featuredProperties, setFeaturedProperties] = useState([]);
  const [linkPoolProperties, setLinkPoolProperties] = useState([]);
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
  const { user } = useAuth();
  const { addToComparison, removeFromComparison, isInComparison } = useComparison();
  const { canEditProperties, canDeleteProperties, canCreateProperties, canAccessAdmin } = usePermissions();
  // Página pública: só o catálogo de labels (sem usageMap que varre properties/condominiums)
  const { catalog: condoCustomCatalog } = useCondoDetailsCustomCatalog(true, { includeUsage: false });
  const condoCustomCatalogMerged = mergeCondoDetailsCustomCatalog(
    condoCustomCatalog,
    property?.condoDetailsCustom
  );
  const { catalog: propertyCustomCatalog } = usePropertyDetailsCustomCatalog(true, { includeUsage: false });
  const propertyCustomCatalogMerged = mergePropertyDetailsCustomCatalog(
    propertyCustomCatalog,
    property?.detailsCustom
  );
  
  // Função auxiliar para verificar se o usuário pode ver ações do imóvel
  const canViewPropertyActions = useCallback((property) => {
    // Se for admin legado, pode ver todas as ações em todos os imóveis
    if (user?.isAdmin) {
      return {
        canEdit: true,
        canDelete: true,
        canDuplicate: true
      };
    }
    
    // Se tem accessAdmin mas não é admin legado, é corretor com acesso ao admin
    // Nesse caso, só pode ver ações nos imóveis vinculados a ele
    if (user && canAccessAdmin()) {
      const propertyBelongsToUser = 
        property.brokerId === user.uid ||
        property.createdBy === user.uid ||
        property.assignedTo === user.uid;
      
      if (propertyBelongsToUser) {
        // Verifica permissões do perfil
        return {
          canEdit: canEditProperties(),
          canDelete: canDeleteProperties(),
          canDuplicate: canCreateProperties() // Duplicar requer permissão de criar
        };
      }
    }
    
    // Se não pertence ao corretor ou não tem acesso, não pode ver ações
    return {
      canEdit: false,
      canDelete: false,
      canDuplicate: false
    };
  }, [user, canAccessAdmin, canEditProperties, canDeleteProperties, canCreateProperties]);
  
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    property: null
  });
  const [userFavorites, setUserFavorites] = useState([]);
  const [shareModal, setShareModal] = useState({
    isOpen: false,
    property: null
  });
  const [quickEditModal, setQuickEditModal] = useState({
    isOpen: false,
    property: null
  });
  const [hasViewed, setHasViewed] = useState(false);
  const settings = useCompanySettings();
  const { companyName } = settings;

  const officialBaseUrl = useMemo(() => resolveOfficialBaseUrl(settings), [settings]);

  const propertySeoSupplementary = useMemo(() => {
    if (!property) return [];
    return [buildPropertyBreadcrumbSchema(property, officialBaseUrl)];
  }, [property, officialBaseUrl]);

  const propertyImageAlts = useMemo(() => {
    if (!property?.images?.length) return [];
    const total = property.images.length;
    return property.images.map((_, index) =>
      generatePropertyImageAlt(property, { index, total })
    );
  }, [property]);

  const internalLinksPool = useMemo(() => {
    const merged = [...linkPoolProperties, ...featuredProperties];
    return merged.filter((p, i, arr) => arr.findIndex((x) => x.id === p.id) === i);
  }, [linkPoolProperties, featuredProperties]);

  // Estados para animações
  const [, setIsLoaded] = useState(false);
  const [visibleElements, setVisibleElements] = useState({
    header: false,
    gallery: false,
    description: false,
    details: false,
    floorPlans: false,
    video: false,
    tour360: false,
    location: false,
    similar: false
  });

  // Refs para os elementos que serão observados
  const headerRef = useRef(null);
  const galleryRef = useRef(null);
  const descriptionRef = useRef(null);
  const detailsRef = useRef(null);
  const floorPlansRef = useRef(null);
  const videoRef = useRef(null);
  const tour360Ref = useRef(null);
  const locationRef = useRef(null);
  const similarRef = useRef(null);
  
  // Refs para cada card de propriedade similar
  const similarPropertyRefs = useRef({});
  
  // Ref para o timer da apresentação automática
  const slideshowTimerRef = useRef(null);
  const progressTimerRef = useRef(null);
  
  // Estado para controlar visibilidade de cada card similar
  const [, setVisibleSimilarCards] = useState({});
  
  // Estados para controle dos preview cards
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const previewContainerRef = useRef(null);
  
  // Função para obter o ano atual
  const getCurrentYear = () => {
    return new Date().getFullYear();
  };

  // Função para carregar favoritos do usuário
  const loadUserFavorites = useCallback(async () => {
    if (!user) {
      // Carrega favoritos do localStorage para usuários não logados
      const localFavorites = JSON.parse(localStorage.getItem('localFavorites') || '[]');
      setUserFavorites(localFavorites);
      return;
    }
    
    try {
      const db = getFirestore();
      const favoritesQuery = query(
        collection(db, 'favorites'),
        where('userId', '==', user.uid)
      );
      const favoritesSnapshot = await getDocs(favoritesQuery);
      const favoriteIds = favoritesSnapshot.docs.map(doc => doc.data().propertyId);
      setUserFavorites(favoriteIds);
    } catch (error) {
      console.error('Erro ao carregar favoritos:', error);
    }
  }, [user]);

  // Função para alternar favorito
  const toggleFavorite = async () => {
    if (!property) return;

    try {
      const isFavorite = userFavorites.includes(property.id);

      if (!user) {
        // Usuário não logado - usa localStorage
        const localFavorites = JSON.parse(localStorage.getItem('localFavorites') || '[]');
        
        if (isFavorite) {
          // Remove dos favoritos locais
          const updatedFavorites = localFavorites.filter(id => id !== property.id);
          localStorage.setItem('localFavorites', JSON.stringify(updatedFavorites));
          setUserFavorites(updatedFavorites);
          showNotification('Imóvel removido dos favoritos', 'warning', 3000);
        } else {
          // Verifica se já tem 20 favoritos
          if (localFavorites.length >= 20) {
            showNotification('Limite de 20 favoritos atingido. Remova alguns para adicionar novos.', 'error', 5000);
            return;
          }

          // Adiciona aos favoritos locais
          const updatedFavorites = [...localFavorites, property.id];
          localStorage.setItem('localFavorites', JSON.stringify(updatedFavorites));
          setUserFavorites(updatedFavorites);
          showNotification('Imóvel adicionado aos favoritos. Faça login para salvar permanentemente.', 'success', 4000);
        }
      } else {
        // Usuário logado - usa Firestore
        const db = getFirestore();

        if (isFavorite) {
          // Remove dos favoritos
          const favoriteQuery = query(
            collection(db, 'favorites'),
            where('userId', '==', user.uid),
            where('propertyId', '==', property.id)
          );
          
          const favoriteSnapshot = await getDocs(favoriteQuery);
          if (!favoriteSnapshot.empty) {
            await deleteDoc(favoriteSnapshot.docs[0].ref);
            setUserFavorites(prev => prev.filter(id => id !== property.id));
            showNotification('Imóvel removido dos favoritos', 'warning', 3000);
          }
        } else {
          // Verifica se já tem 20 favoritos
          if (userFavorites.length >= 20) {
            showNotification('Limite de 20 favoritos atingido. Remova alguns para adicionar novos.', 'error', 5000);
            return;
          }

          // Adiciona aos favoritos
          await addDoc(collection(db, 'favorites'), {
            userId: user.uid,
            propertyId: property.id,
            createdAt: new Date()
          });
          setUserFavorites(prev => [...prev, property.id]);
          showNotification('Imóvel adicionado aos favoritos', 'success', 3000);
        }
      }
    } catch (error) {
      console.error('Erro ao atualizar favoritos:', error);
      showNotification('Erro ao atualizar favoritos', 'error', 5000);
    }
  };

  // Função para verificar se é favorito
  const isFavorite = () => {
    return userFavorites.includes(property?.id);
  };

  // Funções de compartilhamento
  const getPropertyUrl = (property) => {
    return `${window.location.origin}${buildPropertyPath(property)}`;
  };

  const getShareText = (property) => {
    return `Confira este imóvel: ${property.title} - ${getPropertyDisplayAddress(property)}`;
  };

  const shareToWhatsApp = () => {
    if (!property) return;
    trackWhatsAppClick({ source: 'property_share', itemId: property.id, itemType: 'property' });
    const url = getPropertyUrl(property);
    const text = getShareText(property);
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(`${text} ${url}`)}`;
    window.open(whatsappUrl, '_blank');
  };

  const shareToFacebook = () => {
    if (!property) return;
    const url = getPropertyUrl(property);
    const text = getShareText(property);
    const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(text)}&hashtag=${encodeURIComponent('#imoveis #venda #aluguel')}`;
    window.open(facebookUrl, '_blank', 'width=600,height=400');
  };

  const shareToLinkedIn = () => {
    if (!property) return;
    const url = getPropertyUrl(property);
    const text = getShareText(property);
    const linkedinUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}&title=${encodeURIComponent(property.title)}&summary=${encodeURIComponent(text)}`;
    window.open(linkedinUrl, '_blank', 'width=600,height=400');
  };

  const shareToEmail = () => {
    if (!property) return;
    const url = getPropertyUrl(property);
    const text = getShareText(property);
    const subject = `Imóvel: ${property.title}`;
    const body = `${text}\n\nVeja mais detalhes: ${url}`;
    const emailUrl = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = emailUrl;
  };

  const copyLink = async () => {
    if (!property) return;
    const url = getPropertyUrl(property);
    try {
      await navigator.clipboard.writeText(url);
      showNotification('Link copiado para a área de transferência!', 'success', 3000);
    } catch (error) {
      // Fallback para navegadores mais antigos
      const textArea = document.createElement('textarea');
      textArea.value = url;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      showNotification('Link copiado para a área de transferência!', 'success', 3000);
    }
  };

  // Funções para controle das setas
  const checkScrollButtons = () => {
    if (previewContainerRef.current) {
      const container = previewContainerRef.current;
      const canScrollLeft = container.scrollLeft > 0;
      const canScrollRight = container.scrollLeft < container.scrollWidth - container.clientWidth;
      
      setCanScrollLeft(canScrollLeft);
      setCanScrollRight(canScrollRight);
    }
  };

  const scrollPreview = (direction) => {
    if (previewContainerRef.current) {
      const container = previewContainerRef.current;
      const scrollAmount = 200;
      const newPosition = direction === 'left' 
        ? container.scrollLeft - scrollAmount 
        : container.scrollLeft + scrollAmount;
      
      container.scrollTo({
        left: newPosition,
        behavior: 'smooth'
      });
    }
  };

  const handlePreviewScroll = () => {
    checkScrollButtons();
  };

  // Função para prevenir scroll da página principal quando hover nos preview cards
  const handlePreviewWheel = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Verificar se nativeEvent existe antes de usar
    if (e.nativeEvent && e.nativeEvent.stopImmediatePropagation) {
      e.nativeEvent.stopImmediatePropagation();
    }
    
    const container = previewContainerRef.current;
    if (container) {
      container.scrollLeft += e.deltaY;
    }
    return false;
  };

  const handlePropertyView = useCallback(async (propertyData) => {
    if (!propertyData) return;

    const viewKey = `property_view_${propertyData.id}`;
    const lastView = localStorage.getItem(viewKey);
    const now = new Date().getTime();

    if (lastView) {
      const timeDiff = now - parseInt(lastView);
      const hoursDiff = timeDiff / (1000 * 60 * 60);
      
      if (hoursDiff < 12) {
        setHasViewed(true);
        return;
      }
    }

    try {
      const db = getFirestore();
      const propertyRef = doc(db, 'properties', propertyData.id);
      
      await updateDoc(propertyRef, {
        views: increment(1)
      });

      localStorage.setItem(viewKey, now.toString());
      setHasViewed(true);
      trackPropertyView(propertyData);

    } catch (error) {
      console.error('Erro ao registrar visualização:', error);
    }
  }, []);

  useEffect(() => {
    if (property && !hasViewed) {
      handlePropertyView(property);
    }
  }, [property, hasViewed, handlePropertyView]);

  // Carregar favoritos do usuário
  useEffect(() => {
    loadUserFavorites();
  }, [loadUserFavorites]);

  // Função para finalizar o pre-loading
  const handlePreLoadingComplete = () => {
    setIsPreLoading(false);
  };

  useEffect(() => {
    if (!loading && property) {
      setIsPreLoading(false);
    }
  }, [loading, property]);

  useEffect(() => {
    if (!id) return undefined;

    let cancelled = false;
    let idleId = null;
    let timeoutId = null;

    setLoading(true);
    setIsPreLoading(true);
    setProperty(null);
    setLinkPoolProperties([]);
    setFeaturedProperties([]);

    const scheduleIdle = (fn) => {
      if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
        idleId = window.requestIdleCallback(fn, { timeout: 2500 });
      } else {
        timeoutId = setTimeout(fn, 50);
      }
    };

    const loadProperty = async () => {
      try {
        const db = getFirestore();
        const docSnap = await getDoc(doc(db, 'properties', id));
        if (cancelled) return;

        if (!docSnap.exists()) {
          setProperty(null);
          return;
        }

        const propertyData = { id: docSnap.id, ...docSnap.data() };
        let condominiumTitle = '';
        if (propertyData.condominiumId) {
          try {
            const condominiumDocSnap = await getDoc(
              doc(db, 'condominiums', propertyData.condominiumId)
            );
            if (condominiumDocSnap.exists()) {
              condominiumTitle = condominiumDocSnap.data().title || '';
            }
          } catch (condoError) {
            console.warn('Erro ao carregar título do condomínio:', condoError);
          }
        }

        if (cancelled) return;

        setProperty({
          ...propertyData,
          condominiumTitle,
        });
        preloadImages(propertyData.images);

        // Relacionados / destaques: depois do first paint (não bloqueia PreLoader)
        scheduleIdle(async () => {
          if (cancelled) return;
          try {
            const [publicCatalog, homeFeatured] = await Promise.all([
              fetchPropertiesCatalog({ scope: 'public' }),
              fetchHomeFeaturedProperties(12),
            ]);
            if (cancelled) return;

            const citySnapDocs = publicCatalog
              .filter((p) => p.city === propertyData.city)
              .slice(0, 50);
            const typeSnapDocs = propertyData.type
              ? publicCatalog.filter((p) => p.type === propertyData.type).slice(0, 16)
              : [];
            const condominiumSnapDocs = propertyData.condominiumId
              ? publicCatalog
                  .filter((p) => p.condominiumId === propertyData.condominiumId)
                  .slice(0, 16)
              : [];

            const allCityProperties = citySnapDocs.filter((prop) => prop.id !== id);
            const sameTypePool = typeSnapDocs.filter((prop) => prop.id !== id);
            const sameCondominiumPool = condominiumSnapDocs.filter((prop) => prop.id !== id);

            const linkPool = [
              ...allCityProperties,
              ...sameTypePool,
              ...sameCondominiumPool,
            ].filter(
              (prop, index, arr) => arr.findIndex((item) => item.id === prop.id) === index
            );

            setLinkPoolProperties(linkPool);

            const featured = homeFeatured
              .filter(
                (prop) =>
                  prop.id !== id &&
                  prop.isFeatured === true &&
                  prop.showInHome === true &&
                  prop.isAvailable !== false
              )
              .slice(0, 8);

            setFeaturedProperties(
              featured.length > 0
                ? featured
                : homeFeatured.filter((prop) => prop.id !== id).slice(0, 8)
            );
          } catch (enrichError) {
            console.error('Erro ao enriquecer página do imóvel:', enrichError);
          }
        });
      } catch (error) {
        console.error('Erro ao carregar propriedade:', error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadProperty();

    return () => {
      cancelled = true;
      if (idleId != null && typeof window.cancelIdleCallback === 'function') {
        window.cancelIdleCallback(idleId);
      }
      if (timeoutId != null) clearTimeout(timeoutId);
    };
  }, [id]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 640);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  useEffect(() => {
    if (!loading) {
      window.scrollTo(0, 0);
    }
  }, [loading, id]);

  // Parar slideshow quando mudar para tabs de vídeo ou 360º
  useEffect(() => {
    if (showGallery && isSlideshowActive && (galleryTab === 'video' || galleryTab === 'tour360')) {
      stopSlideshow();
    }
  }, [galleryTab, showGallery, isSlideshowActive]);

  // Verificar botões de scroll quando o modal da galeria abre
  useEffect(() => {
    if (showGallery) {
      // Pequeno delay para garantir que o DOM foi renderizado
      setTimeout(() => {
        checkScrollButtons();
      }, 100);

      // Prevenir scroll da página principal quando modal está aberto
      const preventScroll = (e) => {
        e.preventDefault();
        e.stopPropagation();
        return false;
      };

      // Event listener específico para preview cards
      const handlePreviewContainerWheel = (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        // Verificar se nativeEvent existe antes de usar
        if (e.nativeEvent && e.nativeEvent.stopImmediatePropagation) {
          e.nativeEvent.stopImmediatePropagation();
        }
        
        const container = previewContainerRef.current;
        if (container) {
          container.scrollLeft += e.deltaY;
        }
        return false;
      };

      // Adicionar event listeners para prevenir scroll
      document.addEventListener('wheel', preventScroll, { passive: false });
      document.addEventListener('touchmove', preventScroll, { passive: false });

      // Adicionar event listener específico para o container dos preview cards
      const previewContainer = previewContainerRef.current;
      if (previewContainer) {
        previewContainer.addEventListener('wheel', handlePreviewContainerWheel, { passive: false });
      }

      return () => {
        // Remover event listeners quando modal fechar
        document.removeEventListener('wheel', preventScroll);
        document.removeEventListener('touchmove', preventScroll);
        
        if (previewContainer) {
          previewContainer.removeEventListener('wheel', handlePreviewContainerWheel);
        }
      };
    }
  }, [showGallery, property?.images]);


  // Limpar timers quando o componente for desmontado
  useEffect(() => {
    return () => {
      if (slideshowTimerRef.current) {
        clearInterval(slideshowTimerRef.current);
      }
      if (progressTimerRef.current) {
        clearInterval(progressTimerRef.current);
      }
    };
  }, []);

  // Controlar animações de entrada baseadas no scroll
  useEffect(() => {
    // Só inicia as animações após o pre-loading terminar
    if (!isPreLoading) {
      setIsLoaded(true);
      
      // Configuração do Intersection Observer
      const observerOptions = {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
      };

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const elementId = entry.target.getAttribute('data-animate');
            if (elementId) {
              if (elementId === 'similar-property-card') {
                const cardId = entry.target.getAttribute('data-card-id');
                setVisibleSimilarCards(prev => ({ ...prev, [cardId]: true }));
              } else {
                setVisibleElements(prev => ({ ...prev, [elementId]: true }));
              }
              observer.unobserve(entry.target);
            }
          }
        });
      }, observerOptions);

      // Função para configurar o observer
      const setupObserver = () => {
        // Observar elementos principais
      const elementsToObserve = [
        headerRef.current,
        galleryRef.current,
        descriptionRef.current,
        detailsRef.current,
        floorPlansRef.current,
        videoRef.current,
        tour360Ref.current,
        locationRef.current,
        similarRef.current
      ].filter(Boolean);

      elementsToObserve.forEach(element => {
        if (element) {
          observer.observe(element);
        }
      });

      // Observar cada card similar individual
      Object.values(similarPropertyRefs.current).forEach(element => {
        if (element) {
          observer.observe(element);
        }
      });
    };

      // Configurar o observer após um delay
      const timer = setTimeout(setupObserver, 1000);

      return () => {
        clearTimeout(timer);
        observer.disconnect();
      };
    }
  }, [isPreLoading, property, featuredProperties]); // Reconfigurar quando a propriedade ou propriedades similares mudarem

  const preloadImages = (images) => {
    const loadedImages = [];
    images.forEach((src) => {
      const img = new Image();
      img.src = src;
      loadedImages.push(img);
    });
    setPreloadedImages(loadedImages);
  };

  if (loading || !property) {
    return (
      <>
        <PreLoader
          isLoading={isPreLoading}
          onLoadingComplete={handlePreLoadingComplete}
        />
        <PropertyDetailsSkeleton />
      </>
    );
  }

  // Função para determinar quais tabs mostrar
  const getAvailableTabs = () => {
    const tabs = [];
    
    if (property?.images?.length > 0) {
      tabs.push({ id: 'images', label: 'Imagens' });
    }
    
    if (property?.floorPlans?.length > 0) {
      tabs.push({ id: 'floorPlans', label: 'Planta Baixa' });
    }
    
    if (property?.videoUrl) {
      tabs.push({ id: 'video', label: 'Vídeo' });
    }
    
    if (property?.tour360?.type) {
      tabs.push({ id: 'tour360', label: '360º' });
    }
    
    return tabs;
  };

  // Função para obter o conteúdo baseado na tab selecionada
  const getGalleryContent = () => {
    switch (galleryTab) {
      case 'images':
        return property?.images || [];
      case 'floorPlans':
        return property?.floorPlans?.map(plan => plan.image) || [];
      case 'video':
        return property?.videoUrl ? [property.videoUrl] : [];
      case 'tour360':
        return property?.tour360?.type ? ['tour360'] : [];
      default:
        return property?.images || [];
    }
  };

  const openGallery = (index, tab = 'images') => {
    setGalleryTab(tab);
    setCurrentImageIndex(index);
    setShowGallery(true);
    document.body.style.overflow = 'hidden';
    
    // Se abrir nas tabs de vídeo ou 360º, para o slideshow se estiver ativo
    if ((tab === 'video' || tab === 'tour360') && isSlideshowActive) {
      stopSlideshow();
    }
  };

  const closeGallery = () => {
    setShowGallery(false);
    document.body.style.overflow = 'unset';
    stopSlideshow();
  };

  // Função para obter a próxima tab na sequência (para navegação manual)
  const getNextTabForNavigation = (currentTab) => {
    const availableTabs = getAvailableTabs();
    const tabOrder = ['images', 'floorPlans', 'video', 'tour360'];
    
    const currentIndex = tabOrder.indexOf(currentTab);
    
    // Procura a próxima tab disponível
    for (let i = currentIndex + 1; i < tabOrder.length; i++) {
      const nextTab = tabOrder[i];
      if (availableTabs.some(tab => tab.id === nextTab)) {
        return nextTab;
      }
    }
    
    return null; // Não há próxima tab
  };

  // Função para obter a tab anterior na sequência (para navegação manual)
  const getPrevTabForNavigation = (currentTab) => {
    const availableTabs = getAvailableTabs();
    const tabOrder = ['images', 'floorPlans', 'video', 'tour360'];
    
    const currentIndex = tabOrder.indexOf(currentTab);
    
    // Procura a tab anterior disponível
    for (let i = currentIndex - 1; i >= 0; i--) {
      const prevTab = tabOrder[i];
      if (availableTabs.some(tab => tab.id === prevTab)) {
        return prevTab;
      }
    }
    
    return null; // Não há tab anterior
  };

  const nextImage = (e) => {
    e.stopPropagation();
    if (isSlideshowActive) {
      stopSlideshow();
    }
    
    const content = getGalleryContent();
    
    setCurrentImageIndex((prev) => {
      // Se não é o último item da tab atual, vai para o próximo
      if (prev < content.length - 1) {
        return prev + 1;
      }
      
      // Se é o último item, tenta ir para a próxima tab
      const nextTab = getNextTabForNavigation(galleryTab);
      
      if (nextTab) {
        setGalleryTab(nextTab);
        return 0; // Começa do primeiro item da nova tab
      } else {
        // Não há próxima tab, volta para o primeiro item da tab atual
        return 0;
      }
    });
  };

  const prevImage = (e) => {
    e.stopPropagation();
    if (isSlideshowActive) {
      stopSlideshow();
    }
    
    const content = getGalleryContent();
    
    setCurrentImageIndex((prev) => {
      // Se não é o primeiro item da tab atual, vai para o anterior
      if (prev > 0) {
        return prev - 1;
      }
      
      // Se é o primeiro item, tenta ir para a tab anterior
      const prevTab = getPrevTabForNavigation(galleryTab);
      
      if (prevTab) {
        const prevTabContent = getGalleryContentForTab(prevTab);
        setGalleryTab(prevTab);
        return prevTabContent.length - 1; // Vai para o último item da tab anterior
      } else {
        // Não há tab anterior, vai para o último item da tab atual
        return content.length - 1;
      }
    });
  };

  // Função auxiliar para obter conteúdo de uma tab específica
  const getGalleryContentForTab = (tab) => {
    switch (tab) {
      case 'images':
        return property?.images || [];
      case 'floorPlans':
        return property?.floorPlans?.map(plan => plan.image) || [];
      case 'video':
        return property?.videoUrl ? [property.videoUrl] : [];
      case 'tour360':
        return property?.tour360?.type ? ['tour360'] : [];
      default:
        return [];
    }
  };

  const startSlideshow = () => {
    setIsSlideshowActive(true);
    setSlideshowProgress(0);
    
    // Função para animar a barra de progresso
    const animateProgress = () => {
      const startTime = Date.now();
      
      const progressInterval = setInterval(() => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min((elapsed / 3000) * 100, 100);
        setSlideshowProgress(progress);
        
        if (elapsed >= 3000) {
          clearInterval(progressInterval);
        }
      }, 16); // ~60fps
      
      return progressInterval;
    };

    // Função para obter a próxima tab na sequência
    const getNextTab = (currentTab) => {
      const availableTabs = getAvailableTabs();
      const tabOrder = ['images', 'floorPlans', 'video', 'tour360'];
      
      // Se chegou no vídeo ou 360º, para o slideshow
      if (currentTab === 'video' || currentTab === 'tour360') {
        return null;
      }
      
      const currentIndex = tabOrder.indexOf(currentTab);
      const nextIndex = currentIndex + 1;
      
      // Se chegou no final da sequência, retorna null para parar
      if (nextIndex >= tabOrder.length) {
        return null;
      }
      
      // Procura a próxima tab disponível
      for (let i = nextIndex; i < tabOrder.length; i++) {
        const nextTab = tabOrder[i];
        if (availableTabs.some(tab => tab.id === nextTab)) {
          return nextTab;
        }
      }
      
      return null; // Não há mais tabs disponíveis
    };

    // Iniciar primeira animação
    progressTimerRef.current = animateProgress();
    
    // Timer principal para trocar imagens e reiniciar progresso
    slideshowTimerRef.current = setInterval(() => {
      // Verificar se deve parar antes de qualquer coisa
      if (galleryTab === 'video' || galleryTab === 'tour360') {
        stopSlideshow();
        return;
      }
      
      // Limpar timer de progresso anterior
      if (progressTimerRef.current) {
        clearInterval(progressTimerRef.current);
      }
      
      // Obter conteúdo atual baseado na tab atual
      const currentContent = getGalleryContent();
      
      setCurrentImageIndex((prev) => {
        // Se não é o último item da tab atual, vai para o próximo
        if (prev < currentContent.length - 1) {
          return prev + 1;
        }
        
        // Se é o último item da tab atual, tenta ir para a próxima tab
        const nextTab = getNextTab(galleryTab);
        
        if (nextTab) {
          // Usar setTimeout para garantir que a mudança de tab seja processada
          setTimeout(() => {
            setGalleryTab(nextTab);
            setCurrentImageIndex(0);
          }, 0);
          return prev; // Mantém o índice atual temporariamente
        } else {
          // Não há mais tabs, para o slideshow
          stopSlideshow();
          return prev; // Mantém o índice atual
        }
      });
      
      // Reiniciar animação de progresso
      setSlideshowProgress(0);
      progressTimerRef.current = animateProgress();
    }, 3000);
  };

  const stopSlideshow = () => {
    setIsSlideshowActive(false);
    setSlideshowProgress(0);
    
    if (slideshowTimerRef.current) {
      clearInterval(slideshowTimerRef.current);
      slideshowTimerRef.current = null;
    }
    
    if (progressTimerRef.current) {
      clearInterval(progressTimerRef.current);
      progressTimerRef.current = null;
    }
  };

  const toggleSlideshow = (e) => {
    e.stopPropagation();
    if (isSlideshowActive) {
      stopSlideshow();
    } else {
      startSlideshow();
    }
  };

  const getEmbedUrl = (url) => {
    if (!url) return null;

    // Handles URLs like: 
    // https://www.youtube.com/watch?v=VIDEO_ID
    // https://youtu.be/VIDEO_ID
    // https://youtube.com/embed/VIDEO_ID
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);

    if (match && match[2].length === 11) {
      return `https://www.youtube.com/embed/${match[2]}`;
    } else if (url.includes('matterport.com')) {
      return url;
    } else if (url.includes('marzipano.co')) {
      return url;
    }
    return null;
  };

  function formatPrice(value, purpose = 'sale') {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(Number(value)) + (purpose === 'rent' ? '/mês' : '');
  }

  const openDeleteModal = (property) => {
    setDeleteModal({ isOpen: true, property });
  };

  const handleDelete = async () => {
    try {
      const db = getFirestore();
      const auth = getAuth();
      
      // Registrar log antes de deletar
      if (auth.currentUser) {
        await logPropertyDelete(auth.currentUser, deleteModal.property);
      }
      
      await deleteDoc(doc(db, 'properties', deleteModal.property.id));
      navigate('/propriedades');
    } catch (error) {
      console.error('Erro ao excluir:', error);
    }
  };

  const handleDuplicate = (property) => {
    navigate(`/duplicar/${property.id}`);
  };

  const handleQuickEdit = async (changes) => {
    try {
      const db = getFirestore();
      const auth = getAuth();
      
      await setDoc(doc(db, 'properties', property.id), {
        ...property,
        ...changes,
        updatedAt: new Date()
      }, { merge: true });
      
      // Registrar log
      if (auth.currentUser) {
        const changeDiff = buildPropertyChangeDiff(property, { ...property, ...changes });
        await logPropertyUpdate(auth.currentUser, { ...property, ...changes }, { changeDiff });
      }
      
      // Atualiza o estado local
      setProperty(prev => ({ ...prev, ...changes }));
      setQuickEditModal({ isOpen: false, property: null });
    } catch (error) {
      console.error('Erro ao atualizar:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900" style={{ fontFamily: 'Montserrat, sans-serif' }}>
      <style>{`
        .preview-scrollbar::-webkit-scrollbar {
          height: 6px;
        }
        .preview-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .preview-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(255,255,255,0.3);
          border-radius: 3px;
        }
        .preview-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(255,255,255,0.5);
        }
        .scale-102 {
          transform: scale(1.02);
        }
      `}</style>
      {/* PreLoader Component */}
      <PreLoader 
        isLoading={isPreLoading} 
        onLoadingComplete={handlePreLoadingComplete}
      />
      
      {/* SEO Component */}
      {property && (
        <SEO
          title={`${property.title} - ${companyName || 'Imobiliária'}`}
          propertyData={property}
          propertySeo={property.seo}
          ogImage={property.coverImage || (property.images?.length ? property.images[0] : undefined)}
          pageType="product"
          canonicalUrl={buildPropertyUrl(property || { id }, officialBaseUrl)}
          supplementarySchemas={propertySeoSupplementary}
          twitterImageAlt={propertyImageAlts[0] || generatePropertyImageAlt(property)}
          includeSiteSchemas
        />
      )}

      
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Breadcrumbs — visual + BreadcrumbList schema (complementar, não altera schema do imóvel) */}
        <div 
          ref={headerRef}
          data-animate="header"
          className={`mb-8 transition-all duration-1000 ${visibleElements.header ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <BreadcrumbNav
            items={buildPropertyBreadcrumbs(property)}
            baseUrl={officialBaseUrl}
            skipSchema
          />
        </div>

        {/* Cabeçalho */}
        <div 
          className={`flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4 sm:gap-0 mb-8 transition-all duration-1000 delay-200 ${visibleElements.header ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div>
            <h1 className="text-3xl font-montserrat font-semibold text-gray-900 mb-2">{property.title}</h1>
            <div className="text-3xl font-semibold mb-4 sm:hidden" style={{ color: 'var(--primary-color, #3B82F6)' }}>
              {property.purpose === 'rent' 
                ? formatPrice(property.rent, 'rent')
                : formatPrice(property.price)
              }
            </div>
            
            {/* Botões de ação mobile */}
            <div className="flex items-center justify-between sm:hidden mb-4">
              <div className="flex items-center gap-2">
                {/* Favorito */}
                <button
                  onClick={toggleFavorite}
                  className={`p-2 rounded-full transition-all duration-300 hover:scale-110 ${
                    isFavorite() 
                      ? 'text-red-500 bg-red-50' 
                      : 'text-gray-400 hover:text-red-500 hover:bg-red-50'
                  }`}
                  title={isFavorite() ? "Remover dos favoritos" : "Adicionar aos favoritos"}
                >
                  <svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    className="w-5 h-5" 
                    fill={isFavorite() ? "currentColor" : "none"} 
                    viewBox="0 0 24 24" 
                    stroke="currentColor" 
                    strokeWidth={2}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                </button>

                {/* Comparar */}
                <button
                  onClick={async () => {
                    if (isInComparison(property.id)) {
                      await removeFromComparison(property.id);
                      showNotification('Imóvel removido da comparação', 'warning', 3000);
                    } else {
                      const result = await addToComparison(property);
                      if (result.success) {
                        showNotification(result.message, 'success', 3000);
                      } else {
                        showNotification(result.message, 'error', 5000);
                      }
                    }
                  }}
                  className={`p-2 rounded-full transition-all duration-300 hover:scale-110 ${
                    isInComparison(property.id) 
                      ? 'text-blue-400 bg-blue-100' 
                      : 'text-gray-400 hover:text-blue-400 hover:bg-blue-100'
                  }`}
                  title={isInComparison(property.id) ? "Remover da comparação" : "Adicionar à comparação"}
                >
                  <svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    className="w-5 h-5" 
                    fill="none" 
                    viewBox="0 0 24 24" 
                    stroke="currentColor" 
                    strokeWidth={2}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </button>

                {/* Compartilhar */}
                <button
                  onClick={() => setShareModal({ isOpen: true, property })}
                  className="p-2 rounded-full transition-all duration-300 hover:scale-110 text-gray-400 hover:text-green-500 hover:bg-green-50"
                  title="Compartilhar imóvel"
                >
                  <svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    className="w-5 h-5" 
                    fill="none" 
                    viewBox="0 0 24 24" 
                    stroke="currentColor" 
                    strokeWidth={2}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
                  </svg>
                </button>
              </div>

              <span className="px-3 py-1 rounded-full text-sm font-medium" style={{
                backgroundColor: 'var(--secondary-color, #ffffff)',
                color: 'var(--primary-color, #3B82F6)',
                border: '1px solid var(--primary-color, #3B82F6)'
              }}>
                {property.purpose === 'sale' ? 'Venda' : 'Aluguel'}
              </span>
            </div>
            <p className="text-gray-600 flex items-center gap-2 text-sm sm:text-base">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z" />
              </svg>
              {getPropertyDisplayAddress(property)}
            </p>
          </div>
          <div className="text-right hidden sm:block">
            <div className="text-3xl font-semibold mb-2" style={{ color: 'var(--primary-color, #3B82F6)' }}>
              {property.purpose === 'rent' 
                ? formatPrice(property.rent, 'rent')
                : formatPrice(property.price)
              }
            </div>
            <div className="flex items-center justify-end gap-2 mb-2">
              {/* Botões de ação */}
              <div className="flex items-center gap-1">
                {/* Favorito */}
                <button
                  onClick={toggleFavorite}
                  className={`p-2 rounded-full transition-all duration-300 hover:scale-110 ${
                    isFavorite() 
                      ? 'text-red-500 bg-red-100' 
                      : 'text-gray-400 hover:text-red-500 hover:bg-red-50'
                  }`}
                  title={isFavorite() ? "Remover dos favoritos" : "Adicionar aos favoritos"}
                >
                  <svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    className="w-5 h-5" 
                    fill={isFavorite() ? "currentColor" : "none"} 
                    viewBox="0 0 24 24" 
                    stroke="currentColor" 
                    strokeWidth={2}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                </button>

                {/* Comparar */}
                <button
                  onClick={async () => {
                    if (isInComparison(property.id)) {
                      await removeFromComparison(property.id);
                      showNotification('Imóvel removido da comparação', 'warning', 3000);
                    } else {
                      const result = await addToComparison(property);
                      if (result.success) {
                        showNotification(result.message, 'success', 3000);
                      } else {
                        showNotification(result.message, 'error', 5000);
                      }
                    }
                  }}
                  className={`p-2 rounded-full transition-all duration-300 hover:scale-110 ${
                    isInComparison(property.id) 
                      ? 'text-blue-400 bg-blue-100' 
                      : 'text-gray-400 hover:text-blue-400 hover:bg-blue-100'
                  }`}
                  title={isInComparison(property.id) ? "Remover da comparação" : "Adicionar à comparação"}
                >
                  <svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    className="w-5 h-5" 
                    fill="none" 
                    viewBox="0 0 24 24" 
                    stroke="currentColor" 
                    strokeWidth={2}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </button>

                {/* Compartilhar */}
                <button
                  onClick={() => setShareModal({ isOpen: true, property })}
                  className="p-2 rounded-full transition-all duration-300 hover:scale-110 text-gray-400 hover:text-green-500 hover:bg-green-100"
                  title="Compartilhar imóvel"
                >
                  <svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    className="w-5 h-5" 
                    fill="none" 
                    viewBox="0 0 24 24" 
                    stroke="currentColor" 
                    strokeWidth={2}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
                  </svg>
                </button>
              </div>

              <span className="px-3 py-1 rounded-full text-sm font-medium" style={{
                backgroundColor: 'var(--secondary-color, #ffffff)',
                color: 'var(--primary-color, #3B82F6)',
                border: '1px solid var(--primary-color, #3B82F6)'
              }}>
                {property.purpose === 'sale' ? 'Venda' : 'Aluguel'}
              </span>
            </div>
          </div>
        </div>

        {/* Botões de ação - Admin ou Corretor com permissões */}
        {(() => {
          if (!property) return null;
          const actions = canViewPropertyActions(property);
          if (!actions.canEdit && !actions.canDelete && !actions.canDuplicate) {
            return null;
          }
          return (
          <div className="bg-white rounded-lg shadow-sm p-3 sm:p-4 mb-6 flex justify-between sm:justify-end gap-1.5 sm:gap-2">
              {actions.canEdit && (
            <button
              onClick={() => setQuickEditModal({ isOpen: true, property })}
              className="flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 rounded-lg transition-all duration-300 text-sm sm:text-base"
              style={{
                backgroundColor: 'var(--btn-outline-bg, #ffffff)',
                color: 'var(--btn-outline-text, #3B82F6)',
                border: '1px solid var(--btn-outline-border, #3B82F6)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--btn-outline-hover-bg, #3B82F6)';
                e.currentTarget.style.color = 'var(--btn-outline-hover-text, #ffffff)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--btn-outline-bg, #ffffff)';
                e.currentTarget.style.color = 'var(--btn-outline-text, #3B82F6)';
              }}
              title="Editar"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 sm:w-5 sm:h-5">
                <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10" />
              </svg>
              Editar
            </button>
              )}
              {actions.canDuplicate && (
            <button
              onClick={() => handleDuplicate(property)}
              className="flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 rounded-lg transition-all duration-300 text-sm sm:text-base"
              style={{
                backgroundColor: 'var(--btn-outline-bg, #ffffff)',
                color: 'var(--btn-outline-text, #3B82F6)',
                border: '1px solid var(--btn-outline-border, #3B82F6)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--btn-outline-hover-bg, #3B82F6)';
                e.currentTarget.style.color = 'var(--btn-outline-hover-text, #ffffff)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--btn-outline-bg, #ffffff)';
                e.currentTarget.style.color = 'var(--btn-outline-text, #3B82F6)';
              }}
              title="Duplicar"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 sm:w-5 sm:h-5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 17.25v3.375c0 .621-.504 1.125-1.125 1.125h-9.75a1.125 1.125 0 0 1-1.125-1.125V7.875c0-.621.504-1.125 1.125-1.125H6.75a9.06 9.06 0 0 1 1.5.124m7.5 10.376h3.375c.621 0 1.125-.504 1.125-1.125V11.25c0-4.46-3.243-8.161-7.5-8.876a9.06 9.06 0 0 0-1.5-.124H9.375c-.621 0-1.125.504-1.125 1.125v3.5m7.5 10.375H9.375a1.125 1.125 0 0 1-1.125-1.125v-9.25m12 6.625v-1.875a3.375 3.375 0 0 0-3.375-3.375h-1.5a1.125 1.125 0 0 1-1.125-1.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H9.75" />
              </svg>
              Duplicar
            </button>
              )}
              {actions.canDelete && (
            <button
              onClick={() => openDeleteModal(property)}
              className="flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors text-sm sm:text-base border border-red-500"
              title="Remover"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 sm:w-5 sm:h-5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
              </svg>
              Remover
            </button>
        )}
            </div>
          );
        })()}

        {/* Nova Galeria de Imagens */}
        <div 
          ref={galleryRef}
          data-animate="gallery"
          className={`grid grid-cols-1 sm:grid-cols-3 gap-2 mb-8 transition-all duration-1000 delay-400 ${visibleElements.gallery ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          {/* Imagem principal */}
          <div className="sm:col-span-2 h-[300px] sm:h-[600px] relative rounded-lg overflow-hidden cursor-pointer" onClick={() => openGallery(0, 'images')}>
            <LazyLoadImage
              src={property.images[0]}
              alt={propertyImageAlts[0] || property.title}
              className="w-full h-full object-cover"
              wrapperClassName="w-full h-full"
              effect="blur"
              loading="lazy"
              decoding="async"
            />
          </div>

          {/* Grid de miniaturas */}
          <div className="grid grid-cols-4 sm:grid-cols-2 sm:grid-rows-3 gap-2 sm:h-[600px] h-[100px]">
            {/* Em mobile: mostra 4 imagens, última com contador */}
            {/* Em desktop: mostra 6 imagens, última com contador */}
            {property.images.slice(1, isMobile ? 5 : 7).map((image, index) => (
              <div
                key={index}
                className="relative rounded-lg overflow-hidden cursor-pointer h-full"
                onClick={() => openGallery(index + 1, 'images')}
              >
                <LazyLoadImage
                  src={image}
                  alt={propertyImageAlts[index + 1] || `${property.title} ${index + 2}`}
                  className="w-full h-full object-cover"
                  wrapperClassName="w-full h-full"
                  effect="blur"
                  loading="lazy"
                  decoding="async"
                />
                {/* Mostra contador na última imagem */}
                {((isMobile && index === 3) || (!isMobile && index === 5)) && 
                  property.images.length > (isMobile ? 5 : 7) && (
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                    <span className="text-white text-xl font-medium">
                      +{property.images.length - (isMobile ? 5 : 7)}
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Modal de Slideshow */}
        {showGallery && (
          <div
            className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center"
            onClick={closeGallery}
          >
            {/* Barra de progresso - agora no topo */}
            {isSlideshowActive && (
              <div className="absolute top-0 left-0 right-0 h-1 bg-gray-700 z-30">
                <div 
                  className="h-full bg-white transition-all duration-75 ease-linear"
                  style={{ width: `${slideshowProgress}%` }}
                />
              </div>
            )}

            {/* Tabs e Botões de controle - Mobile: separados, Desktop: mesma linha */}
            <div className={`absolute left-4 right-4 z-20 flex justify-between items-center ${isSlideshowActive ? 'top-6' : 'top-4'}`}>
              {/* Tabs centralizadas */}
              <div className="flex-1 flex justify-center">
                <div className="flex bg-black/30 backdrop-blur-sm rounded-lg p-1">
                  {getAvailableTabs().map((tab) => (
                    <button
                      key={tab.id}
                      onClick={(e) => {
                        e.stopPropagation();
                        setGalleryTab(tab.id);
                        setCurrentImageIndex(0);
                        if (isSlideshowActive) {
                          stopSlideshow();
                        }
                      }}
                      className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                        galleryTab === tab.id
                          ? 'bg-white text-black'
                          : 'text-white hover:bg-white/20'
                      }`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Botões de controle - só no desktop */}
              <div className="hidden sm:flex gap-2 ml-4">
              {/* Só mostra o botão de apresentação se não estiver nas tabs de vídeo ou 360º */}
              {(galleryTab !== 'video' && galleryTab !== 'tour360') && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleSlideshow(e);
                  }}
                  onMouseDown={(e) => e.stopPropagation()}
                  className={`text-white p-2 hover:bg-white/10 rounded-full transition-colors z-20 ${
                    isSlideshowActive ? 'bg-white/20' : ''
                  }`}
                  title={isSlideshowActive ? 'Parar apresentação' : 'Iniciar apresentação'}
                  aria-label={isSlideshowActive ? 'Parar apresentação' : 'Iniciar apresentação'}
                  style={{ pointerEvents: 'auto' }}
                >
                  {isSlideshowActive ? (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6" />
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5v14l11-7z" />
                    </svg>
                  )}
                </button>
              )}
              <button
                onClick={closeGallery}
                className="text-white p-2 hover:bg-white/10 rounded-full"
                title="Fechar"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              </div>
            </div>
            
            {/* Botões de controle - só no mobile */}
            <div className={`absolute right-4 flex gap-2 z-20 sm:hidden ${isSlideshowActive ? 'top-24' : 'top-20'}`}>
              {/* Só mostra o botão de apresentação se não estiver nas tabs de vídeo ou 360º */}
              {(galleryTab !== 'video' && galleryTab !== 'tour360') && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleSlideshow(e);
                  }}
                  onMouseDown={(e) => e.stopPropagation()}
                  className={`text-white p-2 hover:bg-white/10 rounded-full transition-colors z-20 ${
                    isSlideshowActive ? 'bg-white/20' : ''
                  }`}
                  title={isSlideshowActive ? 'Parar apresentação' : 'Iniciar apresentação'}
                  aria-label={isSlideshowActive ? 'Parar apresentação' : 'Iniciar apresentação'}
                  style={{ pointerEvents: 'auto' }}
                >
                  {isSlideshowActive ? (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6" />
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5v14l11-7z" />
                    </svg>
                  )}
                </button>
              )}
              <button
                onClick={closeGallery}
                className="text-white p-2 hover:bg-white/10 rounded-full"
                title="Fechar"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <button
              onClick={prevImage}
              className="absolute left-2 sm:left-4 top-1/2 transform -translate-y-1/2 text-white p-2 hover:bg-white/10 rounded-full z-10"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 sm:h-8 sm:w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            <div className={`relative max-w-6xl w-full mx-4 ${isSlideshowActive ? 'mt-20' : 'mt-16'}`} onClick={e => e.stopPropagation()}>
              <div className="relative w-full h-[70vh]">
                {(() => {
                  const content = getGalleryContent();
                  
                  if (galleryTab === 'video') {
                    return (
                      <div className="absolute inset-0 w-full h-full">
                        <iframe
                          className="w-full h-full"
                          src={getEmbedUrl(content[0])}
                          title="Vídeo do Imóvel"
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      </div>
                    );
                  }
                  
                  if (galleryTab === 'tour360') {
                    return (
                      <div className="absolute inset-0 w-full h-full">
                        {property.tour360.type === 'matterport' ? (
                          <iframe
                            className="w-full h-full"
                            src={`https://my.matterport.com/show/?m=${property.tour360.matterportId}`}
                            title="Tour Virtual 3D"
                            frameBorder="0"
                            allowFullScreen
                            allow="xr-spatial-tracking"
                          />
                        ) : (
                          <iframe
                            className="w-full h-full"
                            src={property.tour360.marzipanoUrl}
                            title="Tour Virtual 360°"
                            frameBorder="0"
                            allowFullScreen
                          />
                        )}
                      </div>
                    );
                  }
                  
                  return content.map((src, index) => (
                    <img
                      key={`${galleryTab}-${src}-${index}`}
                      src={src}
                      alt={`${galleryTab === 'floorPlans' ? 'Planta' : 'Slide'} ${index + 1}`}
                      className={`absolute inset-0 w-full h-full object-contain transition-opacity duration-300 ${index === currentImageIndex ? 'opacity-100' : 'opacity-0 pointer-events-none'
                        }`}
                      loading="eager"
                    />
                  ));
                })()}
              </div>
              <div className="absolute bottom-4 left-0 right-0 text-center text-white">
                {currentImageIndex + 1} / {getGalleryContent().length}
              </div>
              
              {/* Preview Cards - só mostra se não for vídeo ou 360º */}
              {(galleryTab !== 'video' && galleryTab !== 'tour360') && (
                <div className="mt-4 px-2 sm:px-4">
                  <div className="bg-black/30 backdrop-blur-sm rounded-lg p-2 sm:p-2 relative">
                  {/* Seta Esquerda */}
                  {canScrollLeft && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        scrollPreview('left');
                      }}
                      className="absolute left-2 top-1/2 transform -translate-y-1/2 z-10 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-colors"
                      title="Anterior"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>
                  )}

                  {/* Seta Direita */}
                  {canScrollRight && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        scrollPreview('right');
                      }}
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 z-10 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-colors"
                      title="Próximo"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  )}

                  <div 
                    ref={previewContainerRef}
                    className="p-1 flex gap-1 sm:gap-2 overflow-x-auto preview-scrollbar"
                    onScroll={handlePreviewScroll}
                    onWheel={handlePreviewWheel}
                    onMouseEnter={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                    }}
                    onMouseLeave={(e) => {
                      e.stopPropagation();
                    }}
                    onMouseMove={(e) => {
                      e.stopPropagation();
                    }}
                    onTouchStart={(e) => {
                      e.stopPropagation();
                    }}
                    onTouchMove={(e) => {
                      e.stopPropagation();
                    }}
                    onTouchEnd={(e) => {
                      e.stopPropagation();
                    }}
                    style={{
                      scrollbarWidth: 'thin',
                      scrollbarColor: 'rgba(255,255,255,0.3) transparent'
                    }}
                  >
                    {getGalleryContent().map((src, index) => (
                      <div
                        key={`${galleryTab}-${src}-${index}`}
                        className={`flex-shrink-0 w-16 h-12 sm:w-28 sm:h-20 rounded-lg overflow-hidden cursor-pointer transition-all duration-300 ${
                          index === currentImageIndex 
                            ? 'ring-2 ring-white scale-105' 
                            : 'hover:scale-102 opacity-70 hover:opacity-100'
                        }`}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (isSlideshowActive) {
                            stopSlideshow();
                          }
                          setCurrentImageIndex(index);
                        }}
                      >
                        {galleryTab === 'video' ? (
                          <div className="w-full h-full bg-gray-800 flex items-center justify-center">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1m4 0h1m-6 4h8m2-10H5a2 2 0 00-2 2v12a2 2 0 002 2h14a2 2 0 002-2V6a2 2 0 00-2-2z" />
                            </svg>
                          </div>
                        ) : galleryTab === 'tour360' ? (
                          <div className="w-full h-full bg-gray-800 flex items-center justify-center">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9v-9m0-9v9" />
                            </svg>
                          </div>
                        ) : (
                          <img
                            src={src}
                            alt={`Preview ${galleryTab === 'floorPlans' ? 'Planta' : 'Slide'} ${index + 1}`}
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              )}
            </div>

            <button
              onClick={nextImage}
              className="absolute right-2 sm:right-4 top-1/2 transform -translate-y-1/2 text-white p-2 hover:bg-white/10 rounded-full z-10"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 sm:h-8 sm:w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        )}

        {/* Área de ícones/características */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
          <div className="flex items-center gap-3 bg-white p-4 rounded-lg shadow-sm">
            <i className="flaticon-bed text-2xl text-gray-400"></i>
            <div>
              <p className="text-sm text-gray-500">Quartos</p>
              <p className="text-lg font-semibold">{property.bedrooms}</p>
            </div>
          </div>

          <div className="flex items-center gap-3 bg-white p-4 rounded-lg shadow-sm">
            <i className="flaticon-bathtub text-2xl text-gray-400"></i>
            <div>
              <p className="text-sm text-gray-500">Banheiros</p>
              <p className="text-lg font-semibold">{property.bathrooms}</p>
            </div>
          </div>

          <div className="flex items-center gap-3 bg-white p-4 rounded-lg shadow-sm">
            <i className="flaticon-car text-2xl text-gray-400"></i>
            <div>
              <p className="text-sm text-gray-500">Vagas</p>
              <p className="text-lg font-semibold">{property.parkingSpaces}</p>
            </div>
          </div>

          <div className="flex items-center gap-3 bg-white p-4 rounded-lg shadow-sm">
            <i className="flaticon-square text-2xl text-gray-400"></i>
            <div>
              <p className="text-sm text-gray-500">Área</p>
              <p className="text-lg font-semibold">{property.area}m²</p>
            </div>
          </div>
        </div>

        {property.purpose === 'sale' && property.showFinancingSimulator !== false && (
          <Suspense fallback={null}>
            <FinancingSimulator
              propertyPrice={property.price}
              propertyTitle={property.title}
            />
          </Suspense>
        )}

        {/* Conteúdo Principal */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Coluna Principal */}
          <div className="lg:col-span-8">
            {/* Descrição */}
            <div 
              ref={descriptionRef}
              data-animate="description"
              className={`bg-white rounded-lg shadow-sm overflow-hidden mb-8 transition-all duration-1000 delay-300 ${visibleElements.description ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            >
              <h3 className="text-xl font-semibold px-6 py-4 border-b">Descrição</h3>
              <div className="p-6">
                <div className="prose max-w-none">
                  <p className="whitespace-pre-line">{property.description}</p>
                </div>
              </div>
            </div>

            {/* Detalhes */}
            <div 
              ref={detailsRef}
              data-animate="details"
              className={`bg-white rounded-lg shadow-sm overflow-hidden mb-8 transition-all duration-1000 delay-400 ${visibleElements.details ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            >
              <div className="px-6 py-4 border-b">
                <h3 className="text-xl font-semibold">Detalhes</h3>
              </div>
              <div className="p-6 space-y-8">
                {/* Detalhes do Imóvel */}
                <div className="border-b pb-8">
                  <h3 className="font-semibold mb-4 text-gray-800">Detalhes do Imóvel</h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                    {/* Informações básicas */}
                    <p className="flex items-center gap-2 text-sm">
                      <span className="text-gray-600">ID:</span>
                      <span className="font-medium">{property.code}</span>
                    </p>
                    <p className="flex items-center gap-2 text-sm">
                      <span className="text-gray-600">Quartos:</span>
                      <span className="font-medium">{property.bedrooms}</span>
                    </p>
                    <p className="flex items-center gap-2 text-sm">
                      <span className="text-gray-600">Suítes:</span>
                      <span className="font-medium">{property.suites}</span>
                    </p>
                    <p className="flex items-center gap-2 text-sm">
                      <span className="text-gray-600">Banheiros:</span>
                      <span className="font-medium">{property.bathrooms}</span>
                    </p>
                    <p className="flex items-center gap-2 text-sm">
                      <span className="text-gray-600">Vagas:</span>
                      <span className="font-medium">{property.parkingSpaces}</span>
                    </p>
                    <p className="flex items-center gap-2 text-sm">
                      <span className="text-gray-600">Área Construída:</span>
                      <span className="font-medium">{property.area}m²</span>
                    </p>

                    {/* IPTU e Condomínio */}
                    <div className="col-span-2 sm:col-span-3 grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {property.iptuFee > 0 && (
                        <p className="flex items-center gap-2 text-sm">
                          <span className="text-gray-600">IPTU:</span>
                          <span className="font-medium">
                            {formatPrice(property.iptuFee)}
                            {property.iptuFrequency === 'monthly' ? '/mês' : '/ano'}
                          </span>
                        </p>
                      )}
                      {property.condoFee > 0 && (
                        <p className="flex items-center gap-2 text-sm">
                          <span className="text-gray-600">Condomínio:</span>
                          <span className="font-medium">{formatPrice(property.condoFee)}/mês</span>
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {getActivePropertyDetailItems(property.details, propertyCustomCatalogMerged).length > 0 && (
                <div className="border-b pb-8">
                  <h3 className="font-semibold mb-4 text-gray-800">Características do Imóvel</h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-y-2">
                    {getActivePropertyDetailItems(property.details, propertyCustomCatalogMerged).map((item) => (
                        <div key={item.key} className="flex items-center gap-2 text-sm text-gray-600">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 shrink-0 text-green-500" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          {item.label}
                        </div>
                      ))}
                  </div>
                </div>
                )}

                {getActiveCondoDetailItems(property.condoDetails, condoCustomCatalogMerged).length > 0 && (
                  <div>
                    <h3 className="font-semibold mb-4 text-gray-800">Características do condomínio</h3>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-y-2">
                      {getActiveCondoDetailItems(property.condoDetails, condoCustomCatalogMerged).map((item) => (
                        <div key={item.key} className="flex items-center gap-2 text-sm text-gray-600">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 shrink-0 text-green-500" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          {item.label}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Localização no Mapa */}
            <div 
              ref={locationRef}
              data-animate="location"
              className={`bg-white rounded-lg shadow-sm overflow-hidden mb-8 transition-all duration-1000 delay-600 ${visibleElements.location ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            >
              <div className="px-6 py-4 border-b">
                <h3 className="text-xl font-semibold mb-1">Localização</h3>
                <p className="text-gray-600 text-sm">
                  {`${property.street}, ${property.number}${property.complement ? `, ${property.complement}` : ''} - ${property.neighborhood}, ${property.city} - ${property.state}`}
                </p>
              </div>
              <div className="p-6">
                <div className="rounded-lg overflow-hidden border border-gray-200">
                  <div className="relative pt-[56.25%]">
                    <iframe
                      className="absolute inset-0 w-full h-full"
                      title="Localização no Google Maps"
                      frameBorder="0"
                      scrolling="no"
                      marginHeight="0"
                      marginWidth="0"
                      src={`https://maps.google.com/maps?q=${encodeURIComponent(`${property.street}, ${property.number}, ${property.neighborhood}, ${property.city}, ${property.state}, Brasil`)}&t=&z=15&ie=UTF8&iwloc=&output=embed`}
                      loading="lazy"
                    ></iframe>
                  </div>
                </div>
              </div>
            </div>

            {/* Plantas */}
            {property.floorPlans?.length > 0 && (
              <div 
                ref={floorPlansRef}
                data-animate="floorPlans"
                className={`bg-white rounded-lg shadow-sm overflow-hidden mb-8 transition-all duration-1000 delay-500 ${visibleElements.floorPlans ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              >
                <h3 className="text-xl font-semibold px-6 py-4 border-b">Plantas</h3>
                <div className="p-6">
                  <div className="space-y-6">
                    {property.floorPlans.map((plan, index) => (
                      <div key={index} className="border rounded-lg p-4">
                        <h3 className="font-semibold mb-2">{plan.name}</h3>
                        {plan.description && (
                          <p className="text-gray-600 mb-4">{plan.description}</p>
                        )}
                        <img
                          src={plan.image}
                          alt={plan.name}
                          className="w-full rounded-lg"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Seção de Vídeo */}
            {property.videoUrl && (
              <div 
                ref={videoRef}
                data-animate="video"
                className={`bg-white rounded-lg shadow-sm overflow-hidden mb-8 transition-all duration-1000 delay-700 ${visibleElements.video ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              >
                <h3 className="text-xl font-semibold px-6 py-4 border-b">Vídeo do Imóvel</h3>
                <div className="p-6">
                  <div className="relative pt-[56.25%] rounded-lg overflow-hidden">
                    <iframe
                      className="absolute inset-0 w-full h-full"
                      src={getEmbedUrl(property.videoUrl)}
                      title="Vídeo do Imóvel"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    ></iframe>
                  </div>
                </div>
              </div>
            )}

            {/* Tour Virtual 360° */}
            {property.tour360?.type && (
              <div 
                ref={tour360Ref}
                data-animate="tour360"
                className={`bg-white rounded-lg shadow-sm overflow-hidden mb-8 transition-all duration-1000 delay-900 ${visibleElements.tour360 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              >
                <h3 className="text-xl font-semibold px-6 py-4 border-b">
                  {property.tour360.type === 'matterport' ? 'Tour Virtual 3D/360°' : property.tour360.type === 'edm360' ? 'Tour Virtual 360°' : 'Tour Virtual 360°'}
                </h3>
                <div className="p-6">
                  <div className="relative pt-[56.25%] rounded-lg overflow-hidden">
                    {property.tour360.type === 'matterport' ? (
                      <iframe
                        className="absolute inset-0 w-full h-full"
                        src={`https://my.matterport.com/show/?m=${property.tour360.matterportId}`}
                        title="Tour Virtual 3D"
                        frameBorder="0"
                        allowFullScreen
                        allow="xr-spatial-tracking"
                      ></iframe>
                    ) : property.tour360.type === 'edm360' ? (
                      <iframe
                        className="absolute inset-0 w-full h-full"
                        src={`https://edm-360-ecbaa.web.app/${property.tour360.edm360FolderName}/index.html`}
                        title="Tour Virtual 360°"
                        frameBorder="0"
                        allowFullScreen
                        style={{ border: 'none' }}
                      ></iframe>
                    ) : (
                      <iframe
                        className="absolute inset-0 w-full h-full"
                        src={property.tour360.marzipanoUrl}
                        title="Tour Virtual 360°"
                        frameBorder="0"
                        allowFullScreen
                      ></iframe>
                    )}
                  </div>
                </div>
              </div>
            )}

          </div>

          {/* Sidebar */}
          <div className="hidden lg:block lg:col-span-4 lg:sticky lg:top-4">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <PropertyContactActions property={property} settings={settings} />

              {/* Outras Propriedades em Destaque */}
              {featuredProperties.length > 0 && (
                <div 
                  ref={similarRef}
                  data-animate="similar"
                  // className="bg-white rounded-lg shadow-sm mt-8 overflow-hidden"
                  className={`bg-white rounded-lg shadow-sm mt-8 overflow-hidden transition-all duration-1000 delay-800`}
                >
                  <h3 className="font-semibold mb-6 px-6 pt-6">Propriedades em Destaque</h3>
                  <div className="space-y-3 -mx-6 px-6 pb-6">
                    {featuredProperties.map((prop) => (
                      <Link
                        key={prop.id}
                        to={buildPropertyPath(prop)}
                        className="group flex gap-2 items-center border-b border-gray-100 last:border-0 pb-4 last:pb-0 transition-all duration-300 hover:bg-gray-50 no-underline text-inherit"
                        onClick={() => {
                          window.scrollTo({ 
                            top: 0,
                            behavior: 'smooth'
                          });
                        }}
                      >
                        <div className="w-[120px] h-20 flex-shrink-0 rounded-lg overflow-hidden">
                          <div className="h-20 w-[120px] transform transition-transform duration-300 hover:scale-105">
                            <LazyLoadImage
                              src={prop.coverImage || prop.images[0]}
                              alt={generatePropertyImageAlt(prop)}
                              className="w-[120px] h-20 object-cover object-center"
                              effect="blur"
                            />
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-gray-900 transition-colors line-clamp-1 group-hover:text-[color:var(--primary-color,#3B82F6)]">
                            {prop.title}
                          </h4>
                          <p className="text-sm text-gray-500 line-clamp-1 mb-1">
                            {prop.neighborhood}, {prop.city}
                          </p>
                          <p className="font-semibold" style={{ color: 'var(--primary-color, #3B82F6)' }}>
                            {prop.purpose === 'rent'
                              ? formatPrice(prop.rent, 'rent')
                              : formatPrice(prop.price)
                            }
                          </p>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Versão Mobile do Formulário e Destaques */}
        <div className="lg:hidden mt-8 space-y-8">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <PropertyContactActions property={property} settings={settings} />
          </div>
          
          {/* Propriedades em Destaque Mobile */}
          {featuredProperties.length > 0 && (
            <div 
              ref={similarRef}
              data-animate="similar"
              className="bg-white rounded-lg shadow-sm p-6"
            >
              <h3 className="font-semibold mb-6">Propriedades em Destaque</h3>
              <div className="space-y-4">
                {featuredProperties.map((prop) => (
                  <Link
                    key={prop.id}
                    to={buildPropertyPath(prop)}
                    className="group flex gap-2 items-center border-b border-gray-100 last:border-0 pb-4 last:pb-0 transition-all duration-300 hover:bg-gray-50 no-underline text-inherit"
                    onClick={() => {
                      window.scrollTo({ 
                        top: 0,
                        behavior: 'smooth'
                      });
                    }}
                  >
                    <div className="w-[120px] h-20 flex-shrink-0 rounded-lg overflow-hidden">
                      <div className="h-20 w-[120px] transform transition-transform duration-300 hover:scale-105">
                        <LazyLoadImage
                          src={prop.coverImage || prop.images[0]}
                          alt={generatePropertyImageAlt(prop)}
                          className="w-[120px] h-20 object-cover object-center"
                          effect="blur"
                        />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-gray-900 transition-colors line-clamp-1 group-hover:text-[color:var(--primary-color,#3B82F6)]">
                        {prop.title}
                      </h4>
                      <p className="text-sm text-gray-500 line-clamp-1 mb-1">
                        {prop.neighborhood}, {prop.city}
                      </p>
                      <p className="font-semibold" style={{ color: 'var(--primary-color, #3B82F6)' }}>
                        {prop.purpose === 'rent'
                          ? formatPrice(prop.rent, 'rent')
                          : formatPrice(prop.price)
                        }
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Links internos automáticos (SEO — complementar) */}
        {property && internalLinksPool.length > 0 && (
          <div className="max-w-7xl mx-auto px-4 py-8">
            <PropertyInternalLinks property={property} allProperties={internalLinksPool} />
          </div>
        )}

        {/* Modal de Confirmação de Exclusão */}
        {deleteModal.isOpen && (
          <div 
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50"
            onClick={() => setDeleteModal({ isOpen: false, property: null })}
          >
            <div 
              className="bg-white rounded-lg p-6 max-w-md w-full mx-4" 
              onClick={e => e.stopPropagation()}
            >
              <h3 className="text-xl font-montserrat font-semibold mb-4">Confirmar Exclusão</h3>
              <p className="text-gray-600 mb-6">
                Tem certeza que deseja excluir a propriedade "<span className="text-black">{deleteModal.property?.title}</span>"?
              </p>
              <div className="flex justify-between gap-3">
                <button
                  onClick={() => setDeleteModal({ isOpen: false, property: null })}
                  className="px-4 py-2 rounded-lg transition-all duration-300"
                  style={{
                    backgroundColor: 'var(--btn-outline-bg, #ffffff)',
                    color: 'var(--btn-outline-text, #3B82F6)',
                    border: '1px solid var(--btn-outline-border, #3B82F6)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--btn-outline-hover-bg, #3B82F6)';
                    e.currentTarget.style.color = 'var(--btn-outline-hover-text, #ffffff)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--btn-outline-bg, #ffffff)';
                    e.currentTarget.style.color = 'var(--btn-outline-text, #3B82F6)';
                  }}
                >
                  Não
                </button>
                <button
                  onClick={handleDelete}
                  className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
                >
                  Sim
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal de Edição Rápida */}
        {quickEditModal.isOpen && (
          <Suspense fallback={null}>
            <QuickEditModal
              isOpen={quickEditModal.isOpen}
              onClose={() => setQuickEditModal({ isOpen: false, property: null })}
              property={quickEditModal.property}
              onSave={handleQuickEdit}
              onAdvancedEdit={() => {
                setQuickEditModal({ isOpen: false, property: null });
                navigate(`/editar/${quickEditModal.property.id}`);
              }}
            />
          </Suspense>
        )}
        
        <ScrollToTop />
      </div>

      {/* Modal de Compartilhamento */}
      {shareModal.isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50"
          onClick={() => setShareModal({ isOpen: false, property: null })}
        >
          <div 
            className="bg-white rounded-lg p-6 max-w-md w-full mx-4" 
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold">Compartilhar Imóvel</h3>
              <button
                onClick={() => setShareModal({ isOpen: false, property: null })}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-500" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>

            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">
                <strong>{shareModal.property?.title}</strong>
              </p>
              <p className="text-xs text-gray-500">
                {getPropertyDisplayAddress(shareModal.property)}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              {/* WhatsApp */}
              <button
                onClick={() => {
                  shareToWhatsApp();
                  setShareModal({ isOpen: false, property: null });
                }}
                className="flex items-center justify-center gap-2 p-3 rounded-lg border border-gray-200 hover:bg-green-50 hover:border-green-300 transition-colors"
              >
                <svg className="w-6 h-6 text-green-600" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                </svg>
                <span className="text-sm font-medium">WhatsApp</span>
              </button>

              {/* Facebook */}
              <button
                onClick={() => {
                  shareToFacebook();
                  setShareModal({ isOpen: false, property: null });
                }}
                className="flex items-center justify-center gap-2 p-3 rounded-lg border border-gray-200 hover:bg-[#1877F2]/10 hover:border-[#1877F2] transition-colors"
              >
                <svg className="w-6 h-6" fill="#1877F2" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
                <span className="text-sm font-medium">Facebook</span>
              </button>

              {/* LinkedIn */}
              <button
                onClick={() => {
                  shareToLinkedIn();
                  setShareModal({ isOpen: false, property: null });
                }}
                className="flex items-center justify-center gap-2 p-3 rounded-lg border border-gray-200 hover:bg-[#0A66C2]/10 hover:border-[#0A66C2] transition-colors"
              >
                <svg className="w-6 h-6" fill="#0A66C2" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
                <span className="text-sm font-medium">LinkedIn</span>
              </button>

              {/* Email */}
              <button
                onClick={() => {
                  shareToEmail();
                  setShareModal({ isOpen: false, property: null });
                }}
                className="flex items-center justify-center gap-2 p-3 rounded-lg border border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-colors"
              >
                <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <span className="text-sm font-medium">Email</span>
              </button>
            </div>

            {/* Copiar Link */}
            <button
              onClick={() => {
                copyLink();
                setShareModal({ isOpen: false, property: null });
              }}
              className="w-full mt-4 flex items-center justify-center gap-2 p-3 rounded-lg border border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-colors"
            >
              <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              <span className="text-sm font-medium">Copiar Link</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default PropertyDetails; 